import React, { useEffect, useState } from "react";
import {
    Modal,
    Box,
    Button,
    TextField,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import { addStudentsCSV, getAllGroups } from "../../../services/adminServices/CoeServices";
import { toast } from "react-toastify";
import { Autocomplete } from "@mui/material";

const FileUploadModal = ({ open, handleClose, coeId, fetchData,fetchCoeData }) => {
    const [isTextField, setIsTextField] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [errors, setErrors] = useState({ group: false, file: false });
    const [groupOptions, setGroupOptions] = useState([]);
    const [uploadResults, setUploadResults] = useState(null);
    const [searchError, setSearchError] = useState("");
    const [uploadLoading, setUploadLoading] = useState(false);
    const [groupLoading, setGroupLoading] = useState(false);

    useEffect(() => {
        const getAllGroupsData = async () => {
            setGroupLoading(true);
            try {
                const response = await getAllGroups();
                setGroupOptions(response?.data?.data || []);
            } catch (err) {
                setGroupOptions([]);
            } finally {
                setGroupLoading(false);
            }
        };
        getAllGroupsData();
    }, []);

    useEffect(() => {
        if (!open) resetAllState();
    }, [open]);

    const resetAllState = () => {
        setInputValue("");
        setSelectedFile(null);
        setIsTextField(false);
        setErrors({ group: false, file: false });
        setUploadResults(null);
        setSearchError("");
        setUploadLoading(false);
    };
    const handleInputChange = (event) => {
        setInputValue(event.target.value);
        setErrors((prev) => ({ ...prev, group: false }));
    };

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setErrors((prev) => ({ ...prev, file: false }));
    };

    const handleSubmit = async () => {
        let newErrors = { group: false, file: false };

        if (!inputValue) newErrors.group = true;
        if (!selectedFile) newErrors.file = true;

        if (newErrors.group || newErrors.file) {
            setErrors(newErrors);
            return;
        }
        setUploadLoading(true);
        try {
            const formData = new FormData();
            formData.append("file", selectedFile);
            const response = await addStudentsCSV(coeId, inputValue, formData);
            
            const responseData = response?.data;
            if (responseData?.code === 207 || (responseData?.failed && responseData.failed.length > 0)) {
                setUploadResults(responseData);
                fetchData();
                fetchCoeData();
            } else {
                resetAllState();
                toast.success(responseData?.message || "File uploaded successfully!");
                fetchData();
                fetchCoeData();
                handleClose();
            }
        } catch (error) {
            const errorData = error?.response?.data;
            if (error?.response?.status === 207 || errorData?.code === 207 || (errorData?.failed && errorData.failed.length > 0)) {
                setUploadResults(errorData);
                fetchData();
                fetchCoeData();
            } else {
                toast.error(errorData?.message || error?.message || "Upload failed.");
            }
        } finally {
            setUploadLoading(false);
        }
    };

    const highlightText = (text, search) => {
        if (!search) return text;
        const parts = String(text).split(new RegExp(`(${search})`, "gi"));
        return parts.map((part, index) =>
            part.toLowerCase() === search.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: "yellow" }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const filteredErrors = uploadResults?.failed?.filter((err) => {
        const searchLower = searchError.toLowerCase();
        return (
            String(err.row).toLowerCase().includes(searchLower) ||
            (err.institution_email && err.institution_email.toLowerCase().includes(searchLower)) ||
            (err.email && err.email.toLowerCase().includes(searchLower)) ||
            (err.reason && err.reason.toLowerCase().includes(searchLower))
        );
    }) || [];

    return (
        <Modal open={open} onClose={uploadLoading ? undefined : handleClose} aria-labelledby="modal-title">
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: uploadResults ? "80%" : 420,
                    minHeight: uploadResults ? "50vh" : "auto",
                    maxHeight: "90vh",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    overflowY: "auto",
                }}
            >
                {uploadResults ? (
                    <>
                        <Typography id="modal-title" variant="h6" textAlign="left" fontWeight="bold" color="text.primary">
                            Upload Summary
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography variant="body1">Total: <strong>{uploadResults.summary?.total || 0}</strong></Typography>
                                <Typography variant="body1" color="success.main">Success: <strong>{uploadResults.summary?.success || 0}</strong></Typography>
                                <Typography variant="body1" color="error">Failed: <strong>{uploadResults.summary?.failed || 0}</strong></Typography>
                            </Box>
                            <TextField
                                size="small"
                                placeholder="Search errors..."
                                value={searchError}
                                onChange={(e) => setSearchError(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        
                        {uploadResults.failed && uploadResults.failed.length > 0 && (
                            <TableContainer component={Paper} sx={{ maxHeight: "55vh", overflow: "auto" }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Row</strong></TableCell>
                                            <TableCell><strong>Institution Email</strong></TableCell>
                                            <TableCell><strong>Email</strong></TableCell>
                                            <TableCell><strong>Reason</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredErrors.length > 0 ? (
                                            filteredErrors.map((err, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{highlightText(err.row, searchError)}</TableCell>
                                                    <TableCell>{highlightText(err.institution_email || "-", searchError)}</TableCell>
                                                    <TableCell>{highlightText(err.email || "-", searchError)}</TableCell>
                                                    <TableCell sx={{ color: 'error.main' }}>{highlightText(err.reason, searchError)}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    No errors found matching "{searchError}"
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => { resetAllState(); handleClose(); }}
                            sx={{ mt: 2 }}
                        >
                            Close
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography id="modal-title" variant="h6" textAlign="left">
                            Upload CSV File
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {!isTextField ? (
                                <Autocomplete
                                    options={groupOptions}
                                    loading={groupLoading}
                                    disabled={uploadLoading}
                                    sx={{ width: "100%" }}
                                    value={inputValue}
                                    onChange={(event, newValue) => setInputValue(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Group Name *"
                                            variant="outlined"
                                            error={errors.group}
                                            helperText={errors.group ? "Please select or enter a group name*" : ""}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {groupLoading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    disabled={uploadLoading}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    label="Enter Group Name * "
                                    variant="outlined"
                                    error={errors.group}
                                    helperText={errors.group ? "Please enter a group name*" : ""}
                                />
                            )}
                            <IconButton onClick={() => setIsTextField(!isTextField)} disabled={uploadLoading}>
                                {isTextField ? <RemoveIcon color="error" /> : <AddIcon color="primary" />}
                            </IconButton>
                        </Box>

                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                label="select File"
                                disabled={uploadLoading}
                                endIcon={<UploadFileIcon color={uploadLoading ? "disabled" : "primary"} />}
                                sx={{
                                    justifyContent: "space-between",
                                    textTransform: "none",
                                    borderRadius: "4px",
                                    width: '100%',
                                    fontSize: "15px",
                                    border: errors.file ? "1px solid #d32f2f" : "1px solid #ccc",
                                    padding: "15px",
                                    color: errors.file ? "#d32f2f" : "hsl(0, 0%, 40%)",
                                }}
                            >
                                {selectedFile ? selectedFile.name : "Choose a CSV File *"}
                                <input type="file" placeholder="select" hidden accept=".csv" onChange={handleFileChange} disabled={uploadLoading} />
                            </Button>
                            {errors.file && <Typography color="error" variant="caption">Please select a CSV file*</Typography>}
                        </Box>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleSubmit}
                            disabled={uploadLoading}
                            startIcon={uploadLoading ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{ py: 1, fontWeight: 600 }}
                        >
                            {uploadLoading ? "Uploading..." : "Submit"}
                        </Button>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default FileUploadModal;